import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import colors from '@c2fo/liquidity/colors';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { parseDate } from './utils';

/**
 * The data required for each chart.
 */
export interface HistoryChartDatum {
  /**
   * The date of the pay period.
   */
  payDate: string;
  /**
   * The estimated date the payment will be deposited.
   */
  depositDate: string;
  /**
   * The weighted average APR for the pay period.
   */
  clearedAprWeightedAvg: number;
  /**
   * The total amount of cleared invoices for the pay period.
   */
  clearedInvoiceAmount: number;
}

export interface HistoryChartProps<T extends HistoryChartDatum> {
  currency?: string;
  data?: Array<T>;
  endDate?: string;
  loading: boolean;
  startDate?: string;
}

const HistoryChart = <T extends HistoryChartDatum>({
  currency = 'USD',
  data = [],
  endDate,
  loading,
  startDate,
}: HistoryChartProps<T>) => {
  const { t } = useTranslation();
  const { asAbbreviatedNumber, asCurrency, asDateString, asDateStringText } = useLocaleFormat();
  // store last valid to display during loading states
  const [lastValidCombinedData, setLastValidCombinedData] = useState<Array<{ x: number; y1: number }>>([]);

  const processedChartData = useMemo(() => {
    if (!startDate || !endDate || data.length === 0) {
      return data.map((item) => ({
        x: parseDate(item.depositDate).valueOf(),
        y1: item.clearedInvoiceAmount,
      }));
    }

    // array of all dates between startDate and endDate
    const start = parseDate(startDate);
    const end = parseDate(endDate);

    // map of existing data points by date for quick lookup
    const dataByDate = new Map();

    // track to handle "include deposit dates in the future"
    // where latest deposit date may be beyond the selected endDate
    let latestPaymentDate = end;

    data.forEach((item) => {
      const itemDate = parseDate(item.depositDate);
      const dateKey = itemDate.valueOf();
      dataByDate.set(dateKey, item.clearedInvoiceAmount);

      if (itemDate > latestPaymentDate) {
        latestPaymentDate = itemDate;
      }
    });

    // generate all dates in range
    const dateRange = [];
    const currentDate = new Date(start);
    while (currentDate <= latestPaymentDate) {
      const dateValue = currentDate.valueOf();
      dateRange.push({
        x: dateValue,
        // use existing data if available, otherwise 0
        y1: dataByDate.has(dateValue) ? dataByDate.get(dateValue) : 0,
      });

      // move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dateRange;
  }, [data, startDate, endDate]);

  // store last valid data to prevent flickering during loading
  useEffect(() => {
    if (!loading) {
      setLastValidCombinedData(processedChartData.length > 0 ? processedChartData : []);
    }
  }, [processedChartData, loading]);

  // determine which data to display - use cached data during loading
  const combinedData = useMemo(
    () => (loading && lastValidCombinedData.length > 0 ? lastValidCombinedData : processedChartData),
    [loading, lastValidCombinedData, processedChartData]
  );

  // check if data to display
  const hasData = useMemo(() => {
    // when loading, always show content to prevent flickering
    if (loading) {
      return true;
    }

    // after loading, check if data
    return (
      data.length > 0 && data.some((item) => item.clearedInvoiceAmount > 0) && combinedData.some((item) => item.y1 > 0)
    );
  }, [combinedData, data, loading]);

  return (
    <ResponsiveContainer height={352}>
      {!hasData ? (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <p className="text-lg font-medium text-text-secondary">{t('core.noDataToDisplay')}</p>
        </div>
      ) : (
        <LineChart data={combinedData} margin={{ top: 0, right: 30, left: 10, bottom: 0 }}>
          <CartesianGrid className="stroke-deepGray-100" vertical={false} />
          <XAxis
            dataKey="x"
            interval={(() => {
              const maxTicks = 10;
              const dataLength = combinedData.length;

              if (dataLength <= maxTicks) {
                return 0;
              }

              return Math.ceil(dataLength / maxTicks) - 1;
            })()}
            tickFormatter={(value) =>
              asDateString(value, {
                day: 'numeric',
                month: 'short',
                year: undefined,
              })
            }
          />
          <YAxis tickFormatter={(value) => asAbbreviatedNumber(value as number)} />
          <Tooltip
            content={({ active, label, payload }) => {
              if (active && payload && payload.length) {
                const value = payload[0].value !== undefined ? +payload[0].value : 0;

                return (
                  <div className="rounded-md border-[0.5px] border-stroke bg-white p-2 text-center shadow-md">
                    <p>{asDateStringText({ date: label, isUTC: false })}</p>
                    <p className="font-medium">{asCurrency(value, currency)}</p>
                  </div>
                );
              }

              return null;
            }}
            cursor={{ fill: colors.gray[100] }}
          />
          <Legend />
          <Line
            dataKey="y1"
            dot={false}
            name={t('core.awardedAmount')}
            stroke={colors.lightBlue[600]}
            strokeWidth={1.5}
            type="monotone"
          />
        </LineChart>
      )}
    </ResponsiveContainer>
  );
};

export default HistoryChart;
